if (!customElements.get("experts-slider-section")) {
  customElements.define(
    "experts-slider-section",
    class ExpertsSliderSection extends HTMLElement {
      constructor() {
        super();
        this._expandedSlide = null;
        this._transitionDuration = 420;
        this._onResize = this._handleResize.bind(this);
      }

      connectedCallback() {
        this._isMobile = window.innerWidth < 750;
        this.initCardExpand();
        this.initNav();
        if (this._isMobile) {
          this.expandAllCards();
        } else {
          this.expandFirstIfNeeded();
        }
        window.addEventListener("resize", this._onResize);
      }

      disconnectedCallback() {
        window.removeEventListener("resize", this._onResize);
      }

      expandFirstIfNeeded() {
        if (!this.hasAttribute("data-expand-first")) return;
        const firstCard = this.querySelector(
          "[data-experts-card][data-has-stats]"
        );
        if (!firstCard) return;
        const slide = firstCard.closest(".experts-slider__slide");
        if (!slide) return;

        const waitForSwiper = () => {
          const slider = this.querySelector("[data-experts-swiper]");
          if (slider && slider.swiper) {
            this.expandCard(firstCard, slide);
          } else {
            requestAnimationFrame(waitForSwiper);
          }
        };
        requestAnimationFrame(waitForSwiper);
      }

      expandAllCards() {
        const waitForSwiper = () => {
          const slider = this.querySelector("[data-experts-swiper]");
          if (slider && slider.swiper) {
            this.querySelectorAll("[data-experts-card][data-has-stats]").forEach(
              (card) => {
                const slide = card.closest(".experts-slider__slide");
                if (slide) {
                  card.classList.add("experts-slider__card--expanded");
                  slide.classList.add("experts-slider__slide--expanded");
                }
              }
            );
            slider.swiper.update();
          } else {
            requestAnimationFrame(waitForSwiper);
          }
        };
        requestAnimationFrame(waitForSwiper);
      }

      /* ---- Card expand / collapse ---- */

      initCardExpand() {
        this.querySelectorAll("[data-experts-card][data-has-stats]").forEach(
          (card) => {
            card.addEventListener("click", (e) => {
              if (this._isMobile) return;
              e.preventDefault();
              this.toggleCard(card);
            });
          }
        );
      }

      toggleCard(card) {
        const slide = card.closest(".experts-slider__slide");
        if (!slide) return;

        const isExpanded = card.classList.contains(
          "experts-slider__card--expanded"
        );

        if (isExpanded) {
          this.collapseCard(card, slide);
        } else {
          if (this._expandedSlide && this._expandedSlide !== slide) {
            const prevCard = this._expandedSlide.querySelector(
              ".experts-slider__card--expanded"
            );
            if (prevCard) {
              this.collapseCard(prevCard, this._expandedSlide, false);
            }
          }
          this.expandCard(card, slide);
        }
      }

      expandCard(card, slide) {
        card.classList.add("experts-slider__card--expanded");
        slide.classList.add("experts-slider__slide--expanded");
        this._expandedSlide = slide;
        this._scheduleSwipUpdate();
      }

      collapseCard(card, slide, updateSwiper = true) {
        card.classList.remove("experts-slider__card--expanded");
        slide.classList.remove("experts-slider__slide--expanded");
        if (this._expandedSlide === slide) {
          this._expandedSlide = null;
        }
        if (updateSwiper) {
          this._scheduleSwipUpdate();
        }
      }

      _scheduleSwipUpdate() {
        const slider = this.querySelector("[data-experts-swiper]");
        if (!slider || !slider.swiper) return;

        requestAnimationFrame(() => {
          slider.swiper.update();
        });

        setTimeout(() => {
          if (slider.swiper) {
            slider.swiper.update();
          }
        }, this._transitionDuration);
      }

      /* ---- Nav buttons ---- */

      initNav() {
        const section = this.closest("[data-section-id]");
        if (!section) return;

        this._sliderEl = section.querySelector("[data-experts-swiper]");
        if (!this._sliderEl) return;

        const prevBtn = section.querySelector(
          ".experts-slider__nav-button--prev"
        );
        const nextBtn = section.querySelector(
          ".experts-slider__nav-button--next"
        );
        if (!prevBtn || !nextBtn) return;

        this._prevBtn = prevBtn;
        this._nextBtn = nextBtn;

        let attempts = 0;
        const maxAttempts = 50;

        const checkSwiper = setInterval(() => {
          if (this._sliderEl.swiper) {
            this._wireButtons();
            clearInterval(checkSwiper);
          } else if (attempts >= maxAttempts) {
            clearInterval(checkSwiper);
          } else {
            attempts++;
          }
        }, 100);
      }

      _getSwiper() {
        return this._sliderEl && this._sliderEl.swiper
          ? this._sliderEl.swiper
          : null;
      }

      _wireButtons() {
        const prevBtn = this._prevBtn;
        const nextBtn = this._nextBtn;

        prevBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const sw = this._getSwiper();
          if (sw && !sw.isLocked && !sw.isBeginning) sw.slidePrev();
        });

        nextBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const sw = this._getSwiper();
          if (sw && !sw.isLocked && !sw.isEnd) sw.slideNext();
        });

        this._updateButtons = () => {
          const sw = this._getSwiper();
          if (!sw) return;
          const locked = sw.isLocked;
          prevBtn.classList.toggle(
            "swiper-button-disabled",
            locked || sw.isBeginning
          );
          prevBtn.disabled = locked || sw.isBeginning;
          nextBtn.classList.toggle(
            "swiper-button-disabled",
            locked || sw.isEnd
          );
          nextBtn.disabled = locked || sw.isEnd;
        };

        const sw = this._getSwiper();
        if (sw) {
          sw.on("slideChange", this._updateButtons);
          sw.on("reachBeginning", this._updateButtons);
          sw.on("reachEnd", this._updateButtons);
          sw.on("resize", this._updateButtons);
        }
        this._updateButtons();
      }

      _handleResize() {
        if (this._updateButtons) this._updateButtons();

        const wasMobile = this._isMobile;
        this._isMobile = window.innerWidth < 750;

        if (wasMobile !== this._isMobile) {
          if (this._isMobile) {
            this.expandAllCards();
          } else {
            this._collapseAllCards();
            this.expandFirstIfNeeded();
          }
        }

        if (this._resizeTimer) clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => {
          const sw = this._getSwiper();
          if (sw) {
            sw.update();
            if (this._updateButtons) this._updateButtons();
          }
        }, 150);
      }

      _collapseAllCards() {
        this.querySelectorAll(".experts-slider__card--expanded").forEach(
          (card) => {
            const slide = card.closest(".experts-slider__slide");
            card.classList.remove("experts-slider__card--expanded");
            if (slide)
              slide.classList.remove("experts-slider__slide--expanded");
          }
        );
        this._expandedSlide = null;
      }
    }
  );
}
